import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import axios from '../../utils/axios';
import {
  MP_LABEL,
  asList,
  forcedItems,
  parseForcedValue,
} from './msAssortmentApi.mjs';

function cabinetKey(marketplace, accountId) {
  return `${marketplace}:${accountId}`;
}

export function MsProductForcedDialog({ open, row, onClose, onSaved, notify }) {
  const [groups, setGroups] = useState([]);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!row?.ms_id) {
      setGroups([]);
      setValues({});
      return;
    }
    setLoading(true);
    try {
      const cabinets = [];
      const seen = new Set();
      for (const link of row.links || []) {
        const key = cabinetKey(link.marketplace, link.account_id);
        if (seen.has(key)) continue;
        seen.add(key);
        cabinets.push({
          marketplace: link.marketplace,
          account_id: link.account_id,
          account_name: link.account_name || link.account_id,
        });
      }

      const [forcedRes, bindingLists] = await Promise.all([
        axios.get(`/ms-assortment/products/${row.ms_id}/forced`, { silent: true }).catch(() => ({ data: { items: forcedItems(row) } })),
        Promise.all(cabinets.map(async (cab) => {
          try {
            const { data } = await axios.get('/ms-assortment/bindings', {
              params: { marketplace: cab.marketplace, account_id: cab.account_id },
            });
            return { ...cab, bindings: asList(data) };
          } catch (_) {
            return { ...cab, bindings: [] };
          }
        })),
      ]);

      const forced = asList(forcedRes.data);
      const nextValues = {};
      for (const item of forced) {
        if (item?.binding_id == null) continue;
        nextValues[String(item.binding_id)] = item.forced_stock == null ? '' : String(item.forced_stock);
      }
      for (const item of forcedItems(row)) {
        const id = String(item.binding_id);
        if (nextValues[id] == null) nextValues[id] = item.forced_stock == null ? '' : String(item.forced_stock);
      }
      setValues(nextValues);
      setGroups(bindingLists);
    } catch (error) {
      notify.error(error?.response?.data?.message || 'Не удалось загрузить принуд');
    } finally {
      setLoading(false);
    }
  }, [row, notify]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const save = async () => {
    const items = [];
    for (const group of groups) {
      for (const binding of group.bindings || []) {
        items.push({
          binding_id: Number(binding.id),
          forced_stock: parseForcedValue(values[String(binding.id)]),
        });
      }
    }
    setSaving(true);
    try {
      await axios.put(`/ms-assortment/products/${row.ms_id}/forced`, { items });
      notify.success('Принуд сохранён');
      onSaved?.();
      onClose();
    } catch (error) {
      notify.error(error?.response?.data?.message || 'Не удалось сохранить принуд');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Принуд · {row?.name || row?.ms_id}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Принуд задаётся на товар МС и складскую связку, не на карточку МП.
            Пустое поле — живой остаток МойСклад.
          </Typography>
          {loading && <Typography variant="body2">Загружаем связки…</Typography>}
          {!loading && !groups.length && (
            <Alert severity="info">Нет связей с кабинетами МП — сначала привяжите карточку.</Alert>
          )}
          {!loading && groups.map((group) => (
            <Stack key={cabinetKey(group.marketplace, group.account_id)} spacing={1}>
              <Typography variant="subtitle2">
                {group.account_name} · {MP_LABEL[group.marketplace] || group.marketplace}
              </Typography>
              {(group.bindings || []).length === 0 && (
                <Typography variant="body2" color="text.secondary">У кабинета нет складских связок</Typography>
              )}
              {(group.bindings || []).map((binding) => (
                <Stack key={binding.id} direction="row" spacing={1} alignItems="center">
                  <TextField
                    size="small"
                    type="number"
                    label={binding.name || `Связка ${binding.id}`}
                    value={values[String(binding.id)] ?? ''}
                    onChange={(e) => setValues((prev) => ({ ...prev, [String(binding.id)]: e.target.value }))}
                    sx={{ flex: 1 }}
                  />
                  <Button
                    size="small"
                    onClick={() => setValues((prev) => ({ ...prev, [String(binding.id)]: '' }))}
                  >
                    Очистить
                  </Button>
                </Stack>
              ))}
            </Stack>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Отмена</Button>
        <Button variant="contained" onClick={save} disabled={saving || loading || !groups.length}>
          {saving ? 'Сохраняем…' : 'Сохранить'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function MsBulkForcedDialog({ open, msIds, onClose, onSaved, notify }) {
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const count = Array.isArray(msIds) ? msIds.length : 0;

  useEffect(() => {
    if (!open) return;
    setValue('');
  }, [open]);

  const apply = async (forcedStock) => {
    if (!count) {
      notify.warning('Выделите товары');
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.put('/ms-assortment/products/forced', {
        ms_ids: msIds,
        forced_stock: forcedStock,
      });
      const applied = Number(data?.applied) || 0;
      const skipped = Array.isArray(data?.skipped) ? data.skipped.length : 0;
      const rows = Number(data?.rows) || 0;
      if (skipped && applied) {
        notify.warning(`Принуд: применено ${applied}, пропущено без связок ${skipped}. Строк: ${rows}`);
      } else if (skipped && !applied) {
        notify.warning(`Принуд не записан: у выбранных товаров нет складских связок (${skipped})`);
      } else {
        notify.success(forcedStock == null
          ? `Принуд снят у ${applied} товаров`
          : `Принуд установлен у ${applied} товаров`);
      }
      onSaved?.();
      onClose();
    } catch (error) {
      notify.error(error?.response?.data?.message || 'Не удалось сохранить принуд');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Принуд для {count} товаров</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Одно значение запишется во все складские связки, к которым выбранные товары уже привязаны.
            Новые связи не создаются. Пустое поле или «Снять принуд» удаляет принуд.
          </Typography>
          <TextField
            size="small"
            type="number"
            label="Принудительный остаток"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            helperText="Оставьте пустым, чтобы снять принуд"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Отмена</Button>
        <Button onClick={() => apply(null)} disabled={saving}>
          Снять принуд
        </Button>
        <Button variant="contained" onClick={() => apply(parseForcedValue(value))} disabled={saving || !count}>
          {saving ? 'Сохраняем…' : `Применить к ${count}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
